import React, { useState } from 'react';
import { connect } from 'react-redux';
import GLOBAL_STYLES from '../styles';
import { app } from '../state/actions';
import { typeToColorMap, getAcronym, getMachinePrefixFromLocator } from '../helpers/misc';
import * as CONFIG from '../config';


const mapStateToProps = state => ({
  colorScheme: state.main.colorScheme,
  highlightedMachine: state.app.highlightedMachine,
  locatorFilter: state.app.locatorFilter,
  timelineViewType: state.app.timelineViewType,
  timelineTextFilter: state.app.timelineTextFilter,
});

const mapDispatchToProps = dispatch => ({
  updateHighlightedMachine: payload => dispatch(app.updateHighlightedMachine(payload)),
  updateOpenMachine: payload => dispatch(app.updateOpenMachine(payload)),
});

const Timeline = props => {
  const {
    colorScheme,
    pixelsPerHour,
    machineObj,
    isLastRow,
    updateHighlightedMachine,
    highlightedMachine,
    locatorFilter,
    updateOpenMachine,
    timelineViewType,
    timelineTextFilter,
  } = props;
  const [lastClick, setLastClick] = useState(0);
  
  const handleNameClickEvent = () => {
    const now = Date.now();
    if(now - lastClick < 500) updateOpenMachine(machineObj);
    else updateHighlightedMachine(machineObj.serialNumber);
    setLastClick(now);
  };
  
  return (
    <div
      style={{
        ...styles.pressContainer,
        borderTopColor: GLOBAL_STYLES[colorScheme].fgColor,
        borderBottomColor: isLastRow ? GLOBAL_STYLES[colorScheme].fgColor : null,
        borderBottomWidth: isLastRow ? 1 : null,
        borderBottomStyle: isLastRow ? 'solid' : null,
      }}
    >
      <div
        style={{
          ...styles.pressNameContainer,
          borderRightColor: GLOBAL_STYLES[colorScheme].fgColor,
        }}
        onClick={handleNameClickEvent}
      >
        <h3
          style={{
            ...styles.pressName,
            color: highlightedMachine === machineObj.serialNumber ? GLOBAL_STYLES.CONSTANTS.orangeColor :
              machineObj?.releasedJobs?.length ? GLOBAL_STYLES.CONSTANTS.yellowColor : GLOBAL_STYLES[colorScheme].fgColor,
          }}
        >
          {
            locatorFilter === 'ALL' ?
              `${getMachinePrefixFromLocator(machineObj.machineLocator)}${machineObj.commonName}` :
              machineObj.commonName
          }
        </h3>
      </div>
      <div style={styles.timelineContainer}>
        {(machineObj.filteredSchedule ? machineObj.filteredSchedule : machineObj.schedule).map((chunkObj, chunkIndex) => {
          let actualChunks = [chunkObj];
          if(!chunkObj.isStoppage && chunkObj.periods) {
            actualChunks = chunkObj.periods;
          }
          let jobData = chunkObj.jobNumbers ?
            machineObj.jobs.find(jobArr => jobArr.some(job => job.jobNumber === chunkObj.jobNumbers[0])) :
            [];
          jobData = jobData ? jobData : [];
          const partNumbers = jobData.map(j => j.partNumber).join(' & ');
          const jobNumbers = jobData.map(j => j.jobNumber).join(' & ');
          const toolNumber = jobData[0]?.toolNumber || '';
          const materialNumbers = jobData.map(j => j.partMaterials ? j.partMaterials.map(m => m.materialNumber) : '').flat().join(' & ');
          const packagingNumber = jobData?.length ? jobData[0].containerPartNumber : '';
          
          return actualChunks.map((aChunkObj, aChunkIndex) => {
            const remainingParts = jobData?.[0]?.startQty ?
              jobData[0]?.actualNetGood ?
                jobData[0].startQty - jobData[0].actualNetGood > 0 ?
                  jobData[0].startQty - jobData[0].actualNetGood :
                  0 :
                jobData[0].startQty :
              null;
            const remainingContainers = remainingParts != null && jobData?.[0]?.containerQuantity ?
              Math.ceil(remainingParts / jobData[0].containerQuantity) :
              null;
            const chunkWidth = ((aChunkObj.endTime - aChunkObj.startTime + 1) / 3600000) * pixelsPerHour;
            const bgColor = typeToColorMap(
              chunkObj.isStoppage ? 'STOPPAGE' : aChunkObj.type === 'DOWN' ? 'DOWN' : 'JOB',
              chunkObj.isCurrent,
              highlightedMachine === machineObj.serialNumber,
              jobData?.length ? jobData[0].unit : null,
            );
            const timelineLabel = aChunkObj.type == null || aChunkObj.type === 'RUNNING' ?
              timelineViewType === 'PART' ?
                partNumbers :
                timelineViewType === 'JOB' ?
                  jobNumbers :
                  timelineViewType === 'TOOL' ?
                    toolNumber :
                    timelineViewType === 'MATERIAL' ?
                      materialNumbers :
                      timelineViewType === 'PACKAGING' ?
                        `${packagingNumber}${remainingContainers != null ? ` (${remainingContainers})` : ''}` :
                        partNumbers :
              '';
            const highlightText = timelineTextFilter.length &&
              ((timelineViewType === 'PART' && partNumbers.toLowerCase().includes(timelineTextFilter.toLowerCase().trim())) ||
                (timelineViewType === 'JOB' && jobNumbers.toLowerCase().includes(timelineTextFilter.toLowerCase().trim())) ||
                (timelineViewType === 'TOOL' && toolNumber.toLowerCase().includes(timelineTextFilter.toLowerCase().trim())) ||
                (timelineViewType === 'MATERIAL' && materialNumbers.toLowerCase().includes(timelineTextFilter.toLowerCase().trim())));
            return (
              <div
                key={`${chunkIndex}|${aChunkIndex}`}
                style={{
                  ...styles.chunk,
                  width: chunkWidth,
                  backgroundColor: bgColor,
                  borderRightColor: GLOBAL_STYLES[colorScheme].fgColor,
                }}
              >
                <p
                  style={{
                    ...styles.chunkText,
                    color: highlightText ?
                      GLOBAL_STYLES.CONSTANTS.redColor :
                      GLOBAL_STYLES.CONSTANTS.white,
                  }}
                >
                  {
                    chunkObj.stoppageReason ?
                      getAcronym(chunkObj.stoppageReason) :
                      timelineLabel
                  }
                </p>
              </div>
            );
          });
        })}
      </div>
    </div>
  );
};

const styles = {
  pressContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    borderTopWidth: 1,
    borderTopStyle: 'solid',
  },
  pressNameContainer: {
    width: CONFIG.MACHINE_NAME_LABEL_WIDTH,
    minWidth: CONFIG.MACHINE_NAME_LABEL_WIDTH,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    cursor: 'pointer',
  },
  pressName: {
    margin: 0,
    padding: 0,
    userSelect: 'none',
  },
  timelineContainer: {
    height: 40,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'no-wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  chunk: {
    height: 40,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    boxSizing: 'border-box',
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    overflow: 'hidden',
  },
  chunkText: {
    margin: 0,
    padding: 0,
    whiteSpace: 'no-wrap',
    marginLeft: 4,
  },
};

export default connect(mapStateToProps, mapDispatchToProps)(Timeline);